import { z } from "zod";
import { safeHttpUrlSchema } from "../safe-url";

export const projectRoleEnum = z.enum([
  "pi",
  "co_pi",
  "lead_researcher",
  "postdoc",
  "collaborator",
  "consultant",
  "other",
]);

export const draftProjectItemSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  title: z.string().max(300), // empty allowed in draft
  role: projectRoleEnum.default("pi"),
  fundingAgency: z.string().max(255).optional(),
  grantNumber: z.string().max(100).optional(),
  amount: z.string().max(100).optional(),
  startDate: z.string().max(50).optional(),
  endDate: z.string().max(50).optional(),
  isCurrent: z.boolean().default(false),
  collaborators: z.string().max(500).optional(),
  description: z.string().max(3000).optional(),
  projectUrl: z.string().max(1024).optional().or(z.literal("")).nullable(),
  imageUrl: z.string().max(1024).optional().or(z.literal("")).nullable(),
  tags: z.array(z.string().max(50)).max(10).default([]),
});

export const projectItemSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  title: z.string().trim().min(1, "Project title is required").max(300),
  role: projectRoleEnum.default("pi"),
  fundingAgency: z.string().max(255).optional(),
  grantNumber: z.string().max(100).optional(),
  amount: z.string().max(100).optional(),
  startDate: z.string().max(50).optional(),
  endDate: z.string().max(50).optional(),
  isCurrent: z.boolean().default(false),
  collaborators: z.string().max(500).optional(),
  description: z.string().max(3000).optional(),
  projectUrl: safeHttpUrlSchema.or(z.literal("")).optional(),
  imageUrl: safeHttpUrlSchema.or(z.literal("")).optional(),
  tags: z.array(z.string().max(50)).max(10).default([]),
});

import { getSectionDisplayOptionsSchema } from "../display-options";

export const projectsDisplayOptionsSchema = getSectionDisplayOptionsSchema("projects");
export type ProjectsDisplayOptions = z.infer<typeof projectsDisplayOptionsSchema>;

export const draftProjectsContentSchema = z.object({
  heading: z.string().max(100).default("Projects & Grants"),
  displayStyle: z.enum(["cards", "list"]).default("cards"),
  items: z.array(draftProjectItemSchema).max(50).default([]),
  displayOptions: projectsDisplayOptionsSchema.optional(),
});



export const projectsContentSchema = z.object({
  heading: z.string().trim().min(1).max(100).default("Projects & Grants"),
  displayStyle: z.enum(["cards", "list"]).default("cards"),
  items: z.array(projectItemSchema).max(50).default([]),
  displayOptions: projectsDisplayOptionsSchema.optional(),
});

export type ProjectRole = z.infer<typeof projectRoleEnum>;
export type ProjectItem = z.infer<typeof projectItemSchema>;
export type DraftProjectItem = z.infer<typeof draftProjectItemSchema>;
export type ProjectsContent = z.infer<typeof projectsContentSchema>;
export type DraftProjectsContent = z.infer<typeof draftProjectsContentSchema>;
